import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  useAuthSessionScenarios,
  PROMPT_BEFORE_IDLE_SECONDS,
} from '@/contexts/hooks/useAuthSessionScenarios';
import { usePermission } from '@/contexts/PermissionContext';
import { deleteCookie, setTenantId, getCookie } from '@/services/api-client';
import {
  getProfile,
  logout as logoutApi,
  getLoginUrl,
  getTenantInfo,
  completeLogin,
} from '@/services/auth-service';
import { fetchUserPermissions } from '@/services/permission-service';
import { loadTenantDateFormat } from '@/services/tenant/tenant-settings-services';
import { RootState } from '@/store';
import { setTenantInfo as setTenantInfoAction } from '@/store/slices/tenant/tenant-slices';
import { getTenantDomain } from '@/utils/domain-utils';
import { showErrorToast } from '@/utils/snackbar-utils';

// Logout reason tracking
enum LogoutReason {
  MANUAL = 'manual',
  IDLE_TIMEOUT = 'idle_timeout',
  TOKEN_REFRESH_FAILED = 'token_refresh_failed',
  COOKIE_MISSING = 'cookie_missing',
  SESSION_EXPIRED = 'session_expired',
  NETWORK_ERROR = 'network_error',
  UNKNOWN = 'unknown',
}

// Helper to get user-friendly message for logout reason
const getLogoutMessage = (reason: LogoutReason): string => {
  switch (reason) {
    case LogoutReason.MANUAL:
      return 'You have been logged out successfully.';
    case LogoutReason.IDLE_TIMEOUT:
      return 'Your session has expired due to inactivity.';
    case LogoutReason.TOKEN_REFRESH_FAILED:
      return 'Session could not be refreshed. Please log in again.';
    case LogoutReason.COOKIE_MISSING:
      return 'Session ended. You were logged out from another tab or device.';
    case LogoutReason.SESSION_EXPIRED:
      return 'Your session has expired. Please log in again.';
    case LogoutReason.NETWORK_ERROR:
      return 'Unable to maintain session due to network issues. Please log in again.';
    default:
      return 'You have been logged out. Please log in again.';
  }
};

const extractNormalizedRole = (response: any): UserRole => {
  const roleValue =
    response?.profile?.data?.role || response?.data?.role || response?.role;
  return roleValue ? (roleValue.toLowerCase() as UserRole) : null;
};

const extractUserData = (response: any): User | null => {
  return response?.profile?.data || response?.data || response || null;
};

const isAuthenticationError = (error: any): boolean => {
  return (
    error?.response?.status === 404 ||
    error?.response?.status === 401 ||
    error?.response?.status === 403 ||
    error?.response?.data?.errorCode === 'ERR006'
  );
};

const getLoginErrorMessage = (err: any): string => {
  const status = err?.response?.status;
  const errorMessage = err?.response?.data?.message;

  if (!err?.response) {
    return 'Network error. Please check your connection and try again.';
  }

  if (status === 401 || status === 403) {
    return errorMessage || 'Unable to initiate login. Please try again.';
  }

  if (status === 404) {
    return errorMessage || 'User not found. Please check your email address.';
  }

  return errorMessage || 'Login failed. Please try again later.';
};

type UserRole = 'admin' | 'tenant' | 'user' | null;

interface User {
  id?: string;
  role: UserRole;
  email?: string;
  name?: string;
  loginDate?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  // The profile API returns this as a role string, e.g. "Admin" / "End User".
  isAdmin?: string;
  // Allow additional properties with unknown type for flexibility
  // while maintaining type safety through explicit checks
}

export interface TenantInfo {
  id?: number;
  companyName?: string;
  makerCheckerPrinciple?: boolean;
  titleTwo?: string;
  titleOne?: string;
  platformSummary?: string;
  bannerFilePath?: string;
  isAdmin?: boolean;
  timezone?: number;
  timezoneName?: string;
  heroDisplayText?: string;
  faviconFilePath?: string;
  lightLogoFilePath?: string;
  darkLogoFilePath?: string;
  lightThemePrimaryColorHex?: string;
  lightThemeSecondaryColorHex?: string;
  darkThemePrimaryColorHex?: string;
  darkThemeSecondaryColorHex?: string;
  authenticationType?: string;
  tenantIdentifier?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  role: UserRole;
  tenantInfo: TenantInfo | null;
  isLoading: boolean;
  isLoginLoading: boolean;
  isTenantInactive: boolean;
  isTenantNotFound: boolean;
  login: () => Promise<void>;
  initiateLogin: (email?: string) => Promise<void>;
  logout: () => void;
  checkAuthStatus: (manualTenantId?: number) => Promise<void>;
  fetchTenantInfo: () => Promise<TenantInfo | null>;
  sessionTimeout: {
    isOpen: boolean;
    remainingSeconds: number;
    onStayLoggedIn: () => void;
    onLogin: () => void;
  };
  logoutSuccess: {
    isOpen: boolean;
    sessionDuration: string;
    remainingSeconds: number;
    onClose: () => void;
    onLogin: () => void;
  };
}

/**
 * Exported for consumers that must tolerate its absence (e.g. TourProvider,
 * which is rendered on its own in tests). Prefer `useAuth`, which throws when
 * the provider is missing.
 */
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tenantInfo = useSelector((state: RootState) => state.tenant.info.data);
  const { setModules } = usePermission();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoginLoading, setIsLoginLoading] = useState<boolean>(false);
  const [isTenantInactive, setIsTenantInactive] = useState<boolean>(false);
  const [isTenantNotFound, setIsTenantNotFound] = useState<boolean>(false);

  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);

  const [openLogoutSuccessModal, setOpenLogoutSuccessModal] = useState(false);
  const [logoutDuration, setLogoutDuration] = useState('');
  const [remainingLogoutSeconds, setRemainingLogoutSeconds] = useState(10);

  const isTenantFetched = React.useRef(false);
  const wasAuthenticated = React.useRef(false);

  const performLogoutRef = React.useRef<
    (...args: any[]) => Promise<void> | void
  >(async () => {});

  // Comment/uncomment this single line to enable/disable consolidated auth session scenarios.
  const { sessionTimeout, activate, setOpenTimeoutModal, setRemainingTime } =
    useAuthSessionScenarios(
      isAuthenticated,
      tenantInfo?.id,
      openLogoutSuccessModal,
      performLogoutRef
    );

  const formatDuration = useCallback((ms: number) => {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const hours = Math.floor(ms / (1000 * 60 * 60));
    if (hours > 0) {
      return `${hours} Hrs : ${minutes} Mins`;
    }
    if (minutes > 0) {
      return `${minutes} Mins : ${seconds} Sec`;
    }
    return `${seconds} Seconds`;
  }, []);

  useEffect(() => {
    let interval: any;
    if (openLogoutSuccessModal && remainingLogoutSeconds > 0) {
      interval = setInterval(() => {
        setRemainingLogoutSeconds(prev => prev - 1);
      }, 1000);
    } else if (openLogoutSuccessModal && remainingLogoutSeconds === 0) {
      // Auto close modal (state already cleared and navigated in performLogout)
      setOpenLogoutSuccessModal(false);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [openLogoutSuccessModal, remainingLogoutSeconds, navigate]);

  const clearAuthState = useCallback(() => {
    setIsAuthenticated(false);
    setRole(null);
    setUser(null);
  }, []);

  const processLogoutQueryParams = useCallback((): boolean => {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const isLogout = urlParams.get('logout') === '1';
    const errorMsg = urlParams.get('error');

    if (!isLogout && !errorMsg) {
      return false;
    }

    if (errorMsg) {
      showErrorToast(decodeURIComponent(errorMsg));
    }

    urlParams.delete('logout');
    urlParams.delete('error');
    const newSearch = urlParams.toString();
    globalThis.history.replaceState(
      {},
      '',
      globalThis.location.pathname + (newSearch ? `?${newSearch}` : '')
    );

    return isLogout;
  }, []);

  const loadUserPermissions = useCallback(
    async (userData: User | null): Promise<void> => {
      if (!userData?.role || userData.role.toLowerCase() === 'admin') {
        return;
      }

      try {
        const permissionResponse = await fetchUserPermissions();
        if (permissionResponse?.success && permissionResponse?.data) {
          setModules(permissionResponse.data);
          return;
        }

        // eslint-disable-next-line no-console
        console.warn('No permission data returned for user');
        setModules([]);
      } catch (permError) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch permissions:', permError);
        setModules([]);
        showErrorToast(
          'Failed to load user permissions. Some features may be unavailable.'
        );
      }
    },
    [setModules]
  );

  const checkAuthStatus = useCallback(
    async (manualTenantId?: number): Promise<void> => {
      try {
        if (processLogoutQueryParams()) {
          clearAuthState();
          return;
        }

        // Optimization: Check if session cookie exists before calling API
        const tId = manualTenantId || tenantInfo?.id;
        const hasSessionCookie = tId ? getCookie(`sv_xid_${tId}`) : true;
        if (!hasSessionCookie) {
          clearAuthState();
          void navigate('/', { replace: true });
          return;
        }

        const response = await getProfile();
        const normalizedRole = extractNormalizedRole(response);
        const userData = extractUserData(response);
        setIsAuthenticated(true);
        setRole(normalizedRole);

        // Set session start time if not already set
        if (!sessionStartTime) {
          setSessionStartTime(Date.now());
        }

        setUser(userData);

        // Tenant portal only: the admin portal has no tenant date format.
        if (!tenantInfo?.isAdmin) void loadTenantDateFormat();

        await loadUserPermissions(userData);

        // Start the idle timer
        activate();
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error('Auth check failed:', error);

        if (isAuthenticationError(error)) {
          // Only show session expired message if user was previously authenticated
          if (wasAuthenticated.current) {
            showErrorToast('Your session has expired. Please log in again.');
            void navigate('/', { replace: true });
          }

          deleteCookie('sv_xid');
          if (tenantInfo?.id) {
            deleteCookie(`sv_xid_${tenantInfo.id}`);
          }
          clearAuthState();
          void navigate('/', { replace: true });
        } else {
          clearAuthState();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      processLogoutQueryParams,
      tenantInfo,
      navigate,
      sessionStartTime,
      loadUserPermissions,
      activate,
      clearAuthState,
    ]
  );

  const fetchTenantInfo = useCallback(async () => {
    // Skip if already fetched
    if (isTenantFetched.current) {
      return null;
    }

    const cached: any = null;
    if (cached) {
      dispatch(setTenantInfoAction(cached));
      setTenantId(cached.id);
      isTenantFetched.current = true;
      return cached;
    }

    try {
      const domain = getTenantDomain();
      const response = await getTenantInfo(domain);
      if (response?.id) {
        dispatch(setTenantInfoAction(response));
        setTenantId(response.id);
        setIsTenantInactive(false);
        setIsTenantNotFound(false);
        isTenantFetched.current = true;
        return response;
      }
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Tenant info fetch failed:', err);
      if (err?.response?.status === 403) {
        setIsTenantInactive(true);
        setIsTenantNotFound(false);
      }
      if (err?.response?.status === 404) {
        setIsTenantNotFound(true);
        setIsTenantInactive(false);
      }
    }
    return null;
  }, [dispatch]);

  const initiateLogin = useCallback(
    async (email?: string) => {
      try {
        setIsLoginLoading(true);

        // Ensure tenant info is fetched (uses ref guard to prevent duplicates)
        let currentTenant = tenantInfo;
        if (!currentTenant) {
          const fetched = await fetchTenantInfo();
          if (fetched) {
            currentTenant = fetched;
          }
        }

        if (currentTenant?.id) {
          const res = await getLoginUrl(currentTenant.id, email);
          globalThis.location.href = res.url;
        }
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('Error initiating login flow:', err);
        showErrorToast(getLoginErrorMessage(err));
      } finally {
        setIsLoginLoading(false);
      }
    },
    [tenantInfo, fetchTenantInfo]
  );

  const login = useCallback(async () => {
    setRemainingTime(PROMPT_BEFORE_IDLE_SECONDS);
    setOpenTimeoutModal(false);
    await checkAuthStatus();
    setSessionStartTime(Date.now());
    wasAuthenticated.current = true;
    activate();
  }, [checkAuthStatus, activate, setRemainingTime, setOpenTimeoutModal]);

  const clearCookiesForSession = useCallback(() => {
    deleteCookie('sv_xid');
    if (tenantInfo?.id) {
      deleteCookie(`sv_xid_${tenantInfo.id}`);
    }
  }, [tenantInfo]);

  const resetAuthSessionState = useCallback(() => {
    setIsAuthenticated(false);
    wasAuthenticated.current = false;
    setRole(null);
    setUser(null);
    setSessionStartTime(null);
  }, []);

  const performLogout = useCallback(
    async (
      isTimeout = false,
      reason: LogoutReason = LogoutReason.MANUAL,
      showErrorMessage = false
    ) => {
      // Log logout event for debugging
      // eslint-disable-next-line no-console
      console.log('[Auth] Logout initiated', {
        isTimeout,
        reason,
        sessionDuration: sessionStartTime ? Date.now() - sessionStartTime : 0,
        timestamp: new Date().toISOString(),
      });

      try {
        await logoutApi();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Logout API call failed:', error);
      } finally {
        // Calculate duration
        let durationStr = '';
        let startTime = sessionStartTime;

        // Use user.loginDate if available as the source of truth for session start
        if (user?.loginDate) {
          const loginDate = new Date(user.loginDate);
          if (!Number.isNaN(loginDate.getTime())) {
            startTime = loginDate.getTime();
          }
        }

        // If startTime is not available (should be rare), fallback to Date.now() to avoid redirect loop or missing page
        if (!startTime) {
          startTime = Date.now();
        }

        if (startTime) {
          const duration = Date.now() - startTime;
          durationStr = formatDuration(duration);
        }

        // Show error message if this is an unexpected logout
        if (showErrorMessage && reason !== LogoutReason.MANUAL) {
          const message = getLogoutMessage(reason);
          showErrorToast(message);
        }

        const shouldHardRedirect =
          reason === LogoutReason.TOKEN_REFRESH_FAILED ||
          reason === LogoutReason.COOKIE_MISSING ||
          reason === LogoutReason.SESSION_EXPIRED;

        if (isTimeout) {
          // Keep modal open, set remaining time to 0 to trigger timeout UI
          setRemainingTime(0);
          setOpenTimeoutModal(true);

          // Clear frontend session state and redirect to landing page
          resetAuthSessionState();
          void navigate('/', { replace: true });

          // Only clear cookie to kill backend session
          clearCookiesForSession();
        } else {
          setOpenTimeoutModal(false);
          setLogoutDuration(durationStr || '0s');
          setRemainingLogoutSeconds(10);
          setOpenLogoutSuccessModal(true);

          resetAuthSessionState();

          clearCookiesForSession();

          void navigate('/', { replace: true });
        }

        if (shouldHardRedirect) {
          void navigate('/', { replace: true });
        }
      }
    },
    [
      navigate,
      sessionStartTime,
      user,
      formatDuration,
      clearCookiesForSession,
      resetAuthSessionState,
      setOpenTimeoutModal,
      setRemainingTime,
    ]
  );

  // Keep ref in sync so the hook always calls the latest performLogout
  performLogoutRef.current = performLogout;

  const logout = useCallback(() => {
    void performLogout(false, LogoutReason.MANUAL);
  }, [performLogout]);

  useEffect(() => {
    if (tenantInfo?.id) {
      setTenantId(tenantInfo.id);
    }
  }, [tenantInfo]);

  const initialized = React.useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const init = async () => {
      const tenantData = await fetchTenantInfo();

      // ── Custom domain auth handoff ─────────────────────────────────────────
      // When the backend redirects back to a custom domain (e.g.
      // jssocustom1.instrive.net) it cannot set sv_xid on that domain directly.
      // Instead it appends ?auth_handoff=<code>. We exchange the code for the
      // CSRF token value and write it as a JS cookie on the current domain so
      // that the existing api-client interceptor picks it up transparently.
      const urlParams = new URLSearchParams(globalThis.location.search);
      const handoffCode = urlParams.get('auth_handoff');

      if (handoffCode && tenantData?.id) {
        try {
          const { csrfToken, expiresAt } = await completeLogin(handoffCode);
          const expires = new Date(expiresAt).toUTCString();
          // Write sv_xid_<tenantId> on the custom domain — same name the
          // api-client interceptor already reads, so no other changes needed.
          document.cookie = `sv_xid_${tenantData.id}=${csrfToken}; path=/; SameSite=Strict; Secure; expires=${expires}`;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[AuthHandoff] Failed to exchange handoff code:', err);
        } finally {
          // Always clean the URL — remove auth_handoff regardless of success/failure
          urlParams.delete('auth_handoff');
          const cleanUrl =
            globalThis.location.pathname +
            (urlParams.toString() ? '?' + urlParams.toString() : '');
          globalThis.history.replaceState({}, '', cleanUrl);
        }
      }
      // ──────────────────────────────────────────────────────────────────────

      const authResult = await checkAuthStatus(tenantData?.id);
      // Track if user becomes authenticated during initialization
      if (authResult !== undefined) {
        wasAuthenticated.current = true;
      }
    };
    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - should only run once on mount

  const handleLoginAfterTimeout = useCallback(() => {
    // Directly initiate login - keep modal open to show loading
    void initiateLogin();
  }, [initiateLogin]);

  const value: AuthContextType = useMemo(
    () => ({
      isAuthenticated,
      user,
      role,
      tenantInfo,
      isLoading,
      isLoginLoading,
      isTenantInactive,
      isTenantNotFound,
      login,
      logout,
      initiateLogin,
      checkAuthStatus,
      fetchTenantInfo,
      sessionTimeout: {
        ...sessionTimeout,
        onLogin: handleLoginAfterTimeout,
      },
      logoutSuccess: {
        isOpen: openLogoutSuccessModal,
        sessionDuration: logoutDuration,
        remainingSeconds: remainingLogoutSeconds,
        onClose: () => {
          setOpenLogoutSuccessModal(false);
        },
        onLogin: () => {
          // Keep modal open to show loading
          void initiateLogin();
        },
      },
    }),
    [
      isAuthenticated,
      user,
      role,
      tenantInfo,
      isLoading,
      isLoginLoading,
      isTenantInactive,
      isTenantNotFound,
      login,
      logout,
      initiateLogin,
      checkAuthStatus,
      fetchTenantInfo,
      sessionTimeout,
      handleLoginAfterTimeout,
      openLogoutSuccessModal,
      logoutDuration,
      remainingLogoutSeconds,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
